// src/components/Header.tsx
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Share2, BookOpen, User, LogOut, Settings, Menu, X } from "lucide-react";
import GradientHeart from "@/components/GradientHeart";
import { useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { User as SupabaseUser, Session } from '@supabase/supabase-js';
import { useEntitlements } from '@/entitlements/useEntitlements';
import { usePricingModal } from '@/context/PricingModalContext';
import { SidebarTrigger } from "@/components/ui/sidebar";
import "../index.css";

export type Timeframe = "month" | "all" | "brand";
interface HeaderProps {
  timeframe?: Timeframe;
  onTimeframeChange?: (value: Timeframe) => void;
  showToggle?: boolean;
}
const Header = ({
  timeframe="all",
  onTimeframeChange,
  showToggle = true
}: HeaderProps) => {
  const [open, setOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const {
    isAuthenticated,
    isSubscribed,
    entitlementLoaded
  } = useEntitlements();
  const {
    openPricingModal
  } = usePricingModal();
  const location = useLocation();
  const navigate = useNavigate();
  const isLibrary = location.pathname.startsWith('/library');
  const toggleValue = isLibrary ? undefined : timeframe;
  const getUserInitials = (name: string, email: string) => {
    const lowerName = name.toLowerCase();
    const lowerEmail = email.toLowerCase();
    if (lowerName.includes('nameer') || lowerEmail.includes('nameer')) {
      return 'N';
    } else if (lowerName.includes('usman') || lowerEmail.includes('usman')) {
      return 'U';
    }

    // Default to first letter of name or email
    return (name || email.split('@')[0] || 'U').charAt(0).toUpperCase();
  };

  // Initialize auth state
  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setSession(session);
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: "Logged out",
        description: "You have been successfully logged out."
      });
    } catch (error) {
      toast({
        title: "Logout failed",
        description: "There was an error logging out.",
        variant: "destructive"
      });
    }
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link copied",
        description: "A shareable link is in your clipboard."
      });
      setOpen(false);
    } catch {
      toast({
        title: "Copy failed",
        description: "Please try again.",
        variant: "destructive" as any
      });
    }
  };
  const handleNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: document.title,
          text: "Check this out",
          url: window.location.href
        });
        setOpen(false);
        return;
      }
    } catch {
      // fall through to clipboard
    }
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link copied",
        description: "Native share not supported. Link copied."
      });
      setOpen(false);
    } catch {
      toast({
        title: "Share failed",
        description: "Could not share the link.",
        variant: "destructive" as any
      });
    }
  };
  const handleShareApple = () => handleNativeShare();
  const handleShareAndroid = () => handleNativeShare();
  return <header className="sticky top-0 z-30 border-b-black/15 border-b bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        
        {/* Logo */}
        <Link to="/library" className="flex items-center hover-lift" aria-label="Go to library">
          <img src="/lovable-uploads/obyect_black_new.png" alt="Obyect logo" className="h-6 sm:h-7 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-3">
          <Button asChild variant={location.pathname.startsWith('/library') ? "secondary" : "ghost"} className="px-3">
            <Link to="/library" aria-label="Open library" className="flex items-center gap-2" aria-current={location.pathname.startsWith('/library') ? 'page' : undefined}>
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Library</span>
            </Link>
          </Button>
          <Button asChild variant={location.pathname.startsWith('/favorites') ? "secondary" : "ghost"} className="px-3">
            <Link to="/favorites" aria-label="Open favorites" className="flex items-center gap-2" aria-current={location.pathname.startsWith('/favorites') ? 'page' : undefined}>
              <GradientHeart className="h-4 w-4" />
              <span className="hidden sm:inline">Favorites</span>
            </Link>
          </Button>
          
          {/* Upgrade Plan Button for authenticated but unsubscribed users */}
          {entitlementLoaded && isAuthenticated && !isSubscribed && <button onClick={openPricingModal} className="btn-glow px-4 py-2 text-sm font-medium text-white rounded-[5px] opacity-100" style={{
          // backdropFilter: 'blur(4px)',
          // background: `radial-gradient(at bottom left, #e2466c, transparent 80%),
          //   radial-gradient(at bottom right, #d088ff, transparent 70%),
          //   radial-gradient(at top left, #765be6, transparent 85%),
          //   radial-gradient(at top right, #bcbdda, transparent 70%)`
          backdropFilter: 'blur(4px)',
          background: 'linear-gradient(to bottom, #70BAFF, #258BFF)',

        }}>
              Upgrade Plan
            </button>}
          
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="p-2" aria-label="Share">
                <Share2 className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Share this view</DialogTitle>
                <DialogDescription>
                  Choose how you want to share.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-2">
                <Button onClick={handleCopy} className="justify-start" variant="secondary">
                  Copy URL
                </Button>
                <Button onClick={handleShareApple} className="justify-start" variant="secondary">
                  Share via Apple
                </Button>
                <Button onClick={handleShareAndroid} className="justify-start" variant="secondary">
                  Share via Android
                </Button>
              </div>
              <DialogFooter />
            </DialogContent>
          </Dialog>
          
          {/* Authentication Section */}
          {user ?
        // Authenticated user menu
        <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="p-2" aria-label="User menu">
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Profile Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2">
                  <LogOut className="h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu> :
        // Non-authenticated user buttons
        <>
              <Button asChild variant="outline" className="px-3">
                <Link to="/auth/signin">Login</Link>
              </Button>
              <button onClick={() => window.location.href = '/auth/signup'} className="px-4 py-2 btn-glow text-sm font-medium text-white rounded-[5px] opacity-100" style={{
            // backdropFilter: 'blur(4px)',
            // background: `radial-gradient(at bottom left, #e2466c, transparent 80%),
            //   radial-gradient(at bottom right, #d088ff, transparent 70%),
            //   radial-gradient(at top left, #765be6, transparent 85%),
            //   radial-gradient(at top right, #bcbdda, transparent 70%)`
            backdropFilter: 'blur(4px)',
            background: 'linear-gradient(to bottom, #70BAFF, #258BFF)',
        }}>
                Sign Up
              </button>
            </>}
        </div>

        {/* Mobile Hamburger Menu */}
        <div className="md:hidden">
          <DropdownMenu open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="p-2" aria-label="Menu">
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background/95 backdrop-blur-md border border-white/10">
              <DropdownMenuItem asChild>
                <Link to="/library" className="flex items-center gap-3 py-3">
                  <BookOpen className="h-5 w-5" />
                  <span>Library</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/favorites" className="flex items-center gap-3 py-3">
                  <GradientHeart className="h-5 w-5" />
                  <span>Favorites</span>
                </Link>
              </DropdownMenuItem>
              
              {/* Upgrade Plan for mobile */}
              {entitlementLoaded && isAuthenticated && !isSubscribed && <DropdownMenuItem onClick={openPricingModal} className="py-3">
                  <div className="flex items-center gap-3">
                    <div className="btn-glow w-5 h-5 rounded bg-gradient-to-br from-primary to-accent"></div>
                    <span>Upgrade Plan</span>
                  </div>
                </DropdownMenuItem>}
              
              <DropdownMenuSeparator />
              
              {user ? <>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-3 py-3">
                      <Settings className="h-5 w-5" />
                      <span>Profile Settings</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-3 py-3">
                    <LogOut className="h-5 w-5" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </> : <>
                  <DropdownMenuItem asChild>
                    <Link to="/auth/signin" className="flex items-center gap-3 py-3">
                      <User className="h-5 w-5" />
                      <span>Login</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/auth/signup" className="flex items-center gap-3 py-3">
                      <User className="h-5 w-5" />
                      <span>Sign Up</span>
                    </Link>
                  </DropdownMenuItem>
                </>}
              
              <DropdownMenuSeparator />
              
              <DropdownMenuItem onClick={handleNativeShare} className="flex items-center gap-3 py-3">
                <Share2 className="h-5 w-5" />
                <span>Share</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>;
};
export default Header;